/**
 * O pedido de impressão da folha de aprovação, do clique até a janela fechar.
 *
 * Papel: aqui ficam `prepararFolha`, que busca as fotos dos itens, e
 * `useFolhaDeAprovacao`, o estado que diz se há uma `FolhaDeAprovacao` a
 * montar, com `imprimir`, que a monta com as fotos já em mãos e cumpre quando a
 * janela de impressão fecha, e `gerar`, que faz as duas etapas em seguida — a
 * folha só aparece com todas as imagens em mãos.
 *
 * Como usar: a tela chama `gerar(pedidos)` e, enquanto `folha` não for `null`,
 * monta `<FolhaDeAprovacao key={folha.geradaEm} … onConcluida={concluir} />`
 * fora do bloco da tela e dá `print:hidden` a todo o resto. `gerar` rejeita
 * quando a busca de uma foto falha, e aí nenhuma folha é montada; quem chama
 * mostra a falha. Os pedidos de entrada vêm de `pedidoGravadoParaFolha` ou de
 * `rascunhoParaFolha`.
 *
 * A promessa de `gerar` só cumpre em `concluir`, e por isso quem a espera fica
 * parado enquanto a janela de impressão está aberta — é o que deixa a caixa de
 * decisão do lote abrir depois da folha, e não por cima dela no papel. Quem
 * dispara a impressão junto com uma gravação deverá tratar a rejeição na hora
 * (`.then(…, …)`), e não só no fim da gravação: rejeitada sem tratamento
 * enquanto a gravação anda, ela vira erro não tratado no navegador.
 *
 * Um `gerar` novo com uma folha ainda montada troca a folha e cumpre a promessa
 * da anterior, que deixou de ser impressa.
 */
import { useCallback, useRef, useState } from 'react';

import { buscarFotoDoItem } from '../api/compras';
import {
  type PedidoNaFolha,
  type PedidoParaFolha,
  buscarFotosDaFolha,
} from '../folha-de-aprovacao';

/** A folha pedida, pronta para montar. */
export interface FolhaPedida {
  /** O instante do pedido de impressão, em ISO 8601; serve também de `key`. */
  geradaEm: string;
  pedidos: PedidoNaFolha[];
}

/**
 * Busca as fotos dos itens que a folha mostra.
 *
 * Quem grava o pedido logo depois de imprimir — o avanço da cotação — deverá
 * esperar esta busca antes de mandar a gravação: ela apaga e reinsere os itens,
 * e a foto pedida no meio dessa troca não acha o item.
 *
 * @param pedidos - Os pedidos, de `pedidoGravadoParaFolha` ou de
 *   `rascunhoParaFolha`.
 * @returns Os pedidos com as fotos; rejeita quando a busca de uma delas falha.
 */
export function prepararFolha(pedidos: readonly PedidoParaFolha[]): Promise<PedidoNaFolha[]> {
  return buscarFotosDaFolha(pedidos, (itemId) => buscarFotoDoItem(itemId));
}

/**
 * Devolve a folha a montar e as funções que a abrem e fecham.
 *
 * @returns `folha`, nula quando não há impressão em curso; `imprimir`, que
 *   monta a folha com as fotos já buscadas por `prepararFolha` e cumpre quando a
 *   janela fecha; `gerar`, que faz as duas coisas em seguida; e `concluir`, para
 *   o `onConcluida` da folha.
 */
export function useFolhaDeAprovacao() {
  const [folha, setFolha] = useState<FolhaPedida | null>(null);
  const avisarFechamento = useRef<(() => void) | null>(null);

  const imprimir = useCallback(async (prontos: PedidoNaFolha[]) => {
    if (prontos.length === 0) return;
    avisarFechamento.current?.();
    await new Promise<void>((cumprir) => {
      avisarFechamento.current = cumprir;
      setFolha({ geradaEm: new Date().toISOString(), pedidos: prontos });
    });
  }, []);

  const gerar = useCallback(
    async (pedidos: readonly PedidoParaFolha[]) => {
      if (pedidos.length === 0) return;
      await imprimir(await prepararFolha(pedidos));
    },
    [imprimir],
  );

  const concluir = useCallback(() => {
    setFolha(null);
    avisarFechamento.current?.();
    avisarFechamento.current = null;
  }, []);

  return { folha, imprimir, gerar, concluir };
}
